import React, { useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { runInAction } from "mobx";
import { observer, useLocalObservable } from "mobx-react";
import FormTextField from "../../components/common/form/FormTextField";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";

export interface CategoryFormModalProps {
  open: boolean;
  /** `null` = create mode */
  categoryId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

function CategoryFormModal({
  open,
  categoryId,
  onClose,
  onSuccess,
}: CategoryFormModalProps) {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const { categoryStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();

  /** Require explicit `edit_category`; do not grant via `all` alone. */
  const canSubmitCategoryUpdate = userStore.hasPermission(
    EmployeeRoles.EDIT_CATEGORY,
    true,
  );

  const isEdit =
    categoryId != null &&
    typeof categoryId === "number" &&
    Number.isFinite(categoryId);

  const form = useLocalObservable(() => ({
    loading: false,
    saving: false,
    name: "",
    priority: "0",
    display: true,
    active: true,
    seoTitle: "",
    seoDescription: "",
    resetForm() {
      this.name = "";
      this.priority = "0";
      this.display = true;
      this.active = true;
      this.seoTitle = "";
      this.seoDescription = "";
    },
  }));

  const saveGreen =
    theme.palette.success?.main ?? theme.palette.primary.main;

  useEffect(() => {
    if (!open) return;

    if (categoryId != null && Number.isFinite(categoryId)) {
      let stale = false;

      void (async () => {
        runInAction(() => {
          form.loading = true;
        });
        try {
          const c = await categoryStore.getCategoryById(categoryId);
          if (stale) return;
          runInAction(() => {
            form.name = c.name ?? "";
            form.priority = String(c.priority ?? 0);
            form.display = Boolean(c.display);
            form.active = Boolean(c.active);
            form.seoTitle = c.seo_title ?? "";
            form.seoDescription = c.seo_description ?? "";
          });
        } catch (err: any) {
          if (!stale) {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Failed to load category. Try again.",
            );
            onClose();
          }
        } finally {
          if (!stale) {
            runInAction(() => {
              form.loading = false;
            });
          }
        }
      })();

      return () => {
        stale = true;
        runInAction(() => {
          form.loading = false;
        });
      };
    }

    runInAction(() => {
      form.resetForm();
    });
    categoryStore.clearCategoryDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoryId]);

  const handleClose = () => {
    form.resetForm();
    onClose();
  };

  const validate = (): boolean => {
    const trimmed = form.name.trim();
    if (!trimmed) {
      showError("Validation", "Name is required.");
      return false;
    }
    if (Number.isNaN(parseInt(form.priority, 10))) {
      showError("Validation", "Priority must be a number.");
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    priority: (() => {
      const n = parseInt(form.priority, 10);
      return Number.isNaN(n) ? 0 : n;
    })(),
    display: form.display,
    active: form.active,
    seo_title: form.seoTitle.trim() || undefined,
    seo_description: form.seoDescription.trim() || undefined,
  });

  const handleSubmit = () => {
    if (!validate()) return;

    const body = buildPayload();

    if (isEdit && categoryId != null) {
      if (!canSubmitCategoryUpdate) {
        showError(
          "Permission denied",
          "You don't have permission to edit categories.",
        );
        return;
      }
      form.saving = true;
      categoryStore
        .updateCategoryRecord(categoryId, body)
        .then(() => {
          showSuccess("Category updated.");
          form.resetForm();
          onClose();
          onSuccess();
        })
        .catch((err: any) => {
          showError(
            "Error",
            err?.response?.data?.message || "Could not update category.",
          );
        })
        .finally(() => {
          form.saving = false;
        });
      return;
    }

    form.saving = true;
    categoryStore
      .createCategoryRecord(body)
      .then(() => {
        showSuccess("Category created.");
        form.resetForm();
        onClose();
        onSuccess();
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message || "Could not create category.",
        );
      })
      .finally(() => {
        form.saving = false;
      });
  };

  const titleMain = isEdit ? "Edit category" : "Add category";

  const sectionHeading = (label: string) => (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
      {label}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown")
          handleClose();
      }}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreenDialog}
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: 2.5 },
            overflow: "hidden",
            maxHeight: fullScreenDialog ? "100%" : "92vh",
            display: "flex",
            flexDirection: "column",
            m: { xs: 0, sm: 2 },
            border: { xs: "none", sm: 1 },
            borderColor: "divider",
            boxShadow: (t) =>
              fullScreenDialog ? "none" : `0 12px 40px ${alpha(t.palette.common.black, 0.08)}`,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          flexShrink: 0,
          gap: 2,
          background: (t) =>
            `linear-gradient(125deg, ${alpha(t.palette.primary.main, 0.12)} 0%, ${alpha(
              t.palette.primary.main,
              0.03,
            )} 48%, ${alpha(t.palette.background.paper, 1)} 100%)`,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 1.2,
              fontWeight: 700,
              color: "primary.main",
              lineHeight: 1.4,
            }}
          >
            {isEdit ? "Update listing" : "Create listing"}
          </Typography>
          <Typography component="h2" variant="h6" fontWeight={800} sx={{ mt: 0.25 }}>
            {titleMain}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 520 }}>
            {isEdit
              ? "Adjust how this category appears and behaves in your catalog."
              : "Give the category a clear name, set priority, and tune visibility for shoppers."}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          size="small"
          sx={{
            mt: -0.5,
            color: "text.secondary",
            "&:hover": { bgcolor: (t) => alpha(t.palette.text.primary, 0.06) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          flex: 1,
          overflow: "auto",
          bgcolor: (t) => alpha(t.palette.grey[500], 0.04),
        }}
      >
        {form.loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              {sectionHeading("Basics")}
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 8 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                  >
                    Name{" "}
                    <Typography component="span" color="error" fontWeight={700}>
                      *
                    </Typography>
                  </Typography>
                  <FormTextField
                    id="cat-name"
                    placeholder="e.g. Apparel, Bags, Accessories"
                    variant="outlined"
                    sx={{ mb: 0 }}
                    value={form.name}
                    onChange={(_, v) => {
                      form.name = v || "";
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                  >
                    Priority{" "}
                    <Typography component="span" color="error" fontWeight={700}>
                      *
                    </Typography>
                  </Typography>
                  <FormTextField
                    id="cat-priority"
                    type="number"
                    placeholder="0"
                    variant="outlined"
                    sx={{ mb: 0 }}
                    value={form.priority}
                    onChange={(_, v) => {
                      form.priority = String(v ?? "");
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                    Lower numbers often surface first; align with your merchandising rules.
                  </Typography>
                </Grid2>
              </Grid2>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              {sectionHeading("Visibility")}
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Storefront
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Show to customers
                      </Typography>
                    </Box>
                    <Switch
                      checked={Boolean(form.display)}
                      inputProps={{
                        "aria-label": "Show category on storefront",
                      }}
                      onClick={() => {
                        runInAction(() => {
                          form.display = !form.display;
                        });
                      }}
                      color="primary"
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Active
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use in admin and APIs
                      </Typography>
                    </Box>
                    <Switch
                      checked={Boolean(form.active)}
                      inputProps={{
                        "aria-label": "Category active in admin and APIs",
                      }}
                      onClick={() => {
                        runInAction(() => {
                          form.active = !form.active;
                        });
                      }}
                      color="primary"
                    />
                  </Box>
                </Grid2>
              </Grid2>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              {sectionHeading("Search & SEO")}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Optional. Improves how the category can appear in search results.
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                  >
                    SEO Title
                  </Typography>
                  <FormTextField
                    id="cat-seo-title"
                    placeholder="Title for search snippets"
                    variant="outlined"
                    sx={{ mb: 0 }}
                    value={form.seoTitle}
                    onChange={(_, v) => {
                      form.seoTitle = v || "";
                    }}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                  >
                    SEO Description
                  </Typography>
                  <FormTextField
                    id="cat-seo-desc"
                    multiline
                    rows={3}
                    placeholder="Short summary for search engines (optional)"
                    variant="outlined"
                    sx={{ mb: 0 }}
                    value={form.seoDescription}
                    onChange={(_, v) => {
                      form.seoDescription = v || "";
                    }}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "flex-end",
          gap: 1.5,
          flexWrap: "wrap",
          bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={form.saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            minWidth: 100,
            borderColor: "divider",
            color: "text.primary",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            form.loading ||
            form.saving ||
            (isEdit && !canSubmitCategoryUpdate)
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            minWidth: 100,
            bgcolor: saveGreen,
            boxShadow: "none",
            "&:hover": {
              bgcolor: saveGreen,
              filter: "brightness(0.92)",
              boxShadow: "none",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default observer(CategoryFormModal);
